// MOSAICO de camas institucionales con radar EKH. Lectura por SITIO autorizado en lote; la cola
// de casos y las prioridades del centro NO se tocan: esto es contexto de signos, no alarmas.
import { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronRight, Radio, WifiOff } from "lucide-react";
import api from "@/lib/api";
import CamaEkhCard from "@/components/ekh/CamaEkhCard";
import EkhDetalleCama from "@/components/ekh/EkhDetalleCama";
import { conSenal } from "@/lib/ekh/vista";

const REFRESCO_MS = 5000;
const CURVAS_VIVAS_MAX = 12;

export default function MosaicoEkh({ sitioId, casos = null, onAbrirCaso = null }) {
  const [data, setData] = useState(null);
  const [fallo, setFallo] = useState(false);
  // H5 · 403/404 = veredicto de alcance: se deja de consultar (sin bucle de reintentos).
  const [sinAcceso, setSinAcceso] = useState(false);
  // VIS · la sección entra COLAPSADA; la elección se conserva durante la visita a la pantalla
  // (los refrescos de datos no la reinician).
  const [abierto, setAbierto] = useState(false);
  const [selId, setSelId] = useState(null);

  const cargar = useCallback(async () => {
    if (!sitioId) return;
    try {
      const { data: d } = await api.get(`/ekh/vista/sitios/${sitioId}/mosaico`);
      setData(d);
      setFallo(false);
    } catch (e) {
      const st = e?.response?.status;
      if (st === 403 || st === 404) setSinAcceso(true);
      setFallo(true);
    }
  }, [sitioId]);

  useEffect(() => {
    if (sinAcceso) return;
    cargar();
    const t = setInterval(cargar, REFRESCO_MS);
    return () => clearInterval(t);
  }, [cargar, sinAcceso]);
  useEffect(() => { setSinAcceso(false); setSelId(null); }, [sitioId]);

  // v2.20.1 · las camas CON señal van primero; las que no reportan conservan su sitio al final.
  const camas = useMemo(() => {
    const filas = data?.camas || [];
    return [...filas].sort((a, b) => Number(conSenal(b)) - Number(conSenal(a)));
  }, [data]);
  const seleccionada = useMemo(
    () => camas.find((c) => (c.cama_id || c.device_id) === selId) || null, [camas, selId]);

  // v2.20.3 · las mini curvas NO dependen de FC/FR ni de la presencia: las amplitudes del canal
  // rápido se consultan en toda cama EKH válida (con cama acreditada). Se conserva el TOPE de
  // suscripciones: un centro grande no dispara una consulta por cama y por segundo.
  const curvasVivas = useMemo(() => {
    const ids = new Set();
    for (const c of camas) {
      if (!c.cama_id || ids.size >= CURVAS_VIVAS_MAX) continue;
      ids.add(c.cama_id);
    }
    return ids;
  }, [camas]);

  if (!sitioId || sinAcceso || (!camas.length && !fallo)) return null;

  return (
    <section data-testid="ekh-mosaico" className="min-w-0 space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <button type="button" data-testid="ekh-mosaico-toggle" onClick={() => setAbierto((v) => !v)}
          aria-expanded={abierto}
          className="inline-flex min-h-9 items-center gap-1.5 text-[13px] font-extrabold uppercase tracking-wider text-[#012472]">
          {abierto ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          Residentes con monitoreo EKH
        </button>
        <span data-testid="ekh-mosaico-estado"
          className={`gv2-chip inline-flex shrink-0 items-center ${fallo ? "gv2-chip-alert" : "gv2-chip-live"}`}>
          {fallo ? <WifiOff className="h-3.5 w-3.5" /> : <Radio className="h-3.5 w-3.5" />}
          {fallo ? "SIN ACTUALIZAR" : `${data?.lectura?.con_datos ?? 0}/${data?.lectura?.equipos ?? 0} con lectura`}
        </span>
      </div>

      {/* Colapsar sólo OCULTA: el contenido sigue montado, así las lecturas periódicas, las
          suscripciones de curvas y el procesamiento de alertas no se interrumpen. */}
      <div className={abierto ? "" : "hidden"} aria-hidden={!abierto}>
        <div className="flex min-w-0 flex-col gap-3 lg:flex-row lg:items-start lg:overflow-hidden">
          {/* H6 · con la ficha abierta el mosaico cede ancho: menos columnas y más legibles, en
              vez de cuatro columnas estrechas con el nombre truncado. */}
          <div data-testid="ekh-mosaico-grid"
            data-ficha={seleccionada?.cama_id ? "abierta" : "cerrada"}
            className={`grid min-w-0 flex-1 grid-cols-1 gap-2 sm:grid-cols-2 ${
              seleccionada?.cama_id ? "xl:grid-cols-2" : "xl:grid-cols-3 2xl:grid-cols-4"}`}>
            {camas.map((c) => (
              <CamaEkhCard key={c.device_id} fila={c}
                curvaActiva={curvasVivas.has(c.cama_id)}
                seleccionada={(c.cama_id || c.device_id) === selId}
                onSeleccionar={(f) => setSelId(
                  (f.cama_id || f.device_id) === selId ? null : (f.cama_id || f.device_id))} />
            ))}
            {!camas.length && (
              <p data-testid="ekh-mosaico-vacio" className="col-span-full rounded-xl border border-dashed border-slate-200 p-6 text-center text-xs text-slate-400">
                Este centro no tiene radares EKH registrados o su lectura no se pudo consultar.
              </p>
            )}
          </div>
          {seleccionada?.cama_id && (
            <EkhDetalleCama key={seleccionada.cama_id} fila={seleccionada} onCerrar={() => setSelId(null)}
              casos={casos} onAbrirCaso={onAbrirCaso} />
          )}
        </div>
      </div>
    </section>
  );
}
